import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../store/UserContext';
import { t } from '../i18n';
import CloseIcon from './icons/CloseIcon';
import { createMenuItems, hasMenuPermission, isMenuActive } from '../config/navMenu';

function Sidebar({ mobileOpen, setMobileOpen, sidebarOpen = true }) {
  const location = useLocation();
  const { user } = useUser();

  if (!user) return null;

  const menuItems = createMenuItems(user, t);

  const renderMenuItem = (item) => {
    const permitted = hasMenuPermission(item, user);
    const active = isMenuActive(item, location.pathname);

    if (!permitted) {
      return (
        <span
          key={item.path}
          className="block cursor-not-allowed opacity-40 text-gray-400 px-2 py-2 select-none flex items-center justify-center md:justify-start rounded-lg"
          title={!sidebarOpen ? item.title : "无权限"}
        >
          <div className={`flex items-center ${sidebarOpen ? 'justify-center md:justify-start' : 'justify-center'}`}>
            {item.icon && <item.icon className="w-5 h-5 md:w-4 md:h-4 md:mr-2" />}
            <span className={`hidden text-sm ${sidebarOpen ? 'md:inline' : 'md:hidden'}`}>{item.title}</span>
          </div>
        </span>
      );
    }

    const linkClassName = `group block px-2 py-2 rounded-lg transition-all duration-200 ${active
      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
      }`;
    const linkContent = (
      <div className={`flex items-center ${sidebarOpen ? 'justify-center md:justify-start' : 'justify-center'}`}>
        {item.icon && <item.icon className={`w-5 h-5 md:w-4 md:h-4 md:mr-2 ${active ? 'text-white' : 'group-hover:scale-110'} transition-transform duration-200`} />}
        <span className={`hidden text-sm font-medium ${sidebarOpen ? 'md:inline' : 'md:hidden'}`}>{item.title}</span>
      </div>
    );

    if (item.openInNewTab) {
      return (
        <a
          key={item.path}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
          title={!sidebarOpen ? item.title : ''}
          onClick={() => setMobileOpen(false)}
        >
          {linkContent}
        </a>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        className={linkClassName}
        title={!sidebarOpen ? item.title : ''}
      >
        {linkContent}
      </Link>
    );
  };

  return (
    <aside className={
      `w-16 md:w-[168px] flex-shrink-0 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 text-gray-900 dark:text-gray-100 shadow-lg border-r border-gray-200 dark:border-gray-700 z-40 p-2 transition-all duration-300 overflow-y-auto
      ${mobileOpen ? 'fixed top-[50px] left-0 h-[calc(100vh-50px)]' : 'hidden md:block md:relative md:top-0 md:left-0 md:h-full'}
      ${!sidebarOpen ? 'md:w-16' : 'md:w-[168px]'}`
    }>
      <div className="md:hidden flex justify-center mb-2">
        <button
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors shadow-sm"
          onClick={() => setMobileOpen(false)}
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      </div>
      <nav className="flex-1">
        <ul className="space-y-1.5">
          {menuItems.map(item => (
            <li key={item.path}>
              {renderMenuItem(item)}
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}

export default Sidebar;
